import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, RefreshCw, MessageCircle, Link2, FileDown, Power } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PublicCatalogueLink = () => {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/catalogue/public-link`).then(r => setLink(r.data)).catch(() => {});
  }, []);

  if (!link) return null;

  const pageUrl = `${window.location.origin}${link.path}`;
  const pdfUrl = `${API_URL}${link.pdf_path}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error('Copie impossible — sélectionnez le texte manuellement');
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Régénérer le lien ? L'ancien lien ne fonctionnera plus pour personne.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/catalogue/public-link/regenerate`);
      setLink(r.data);
      toast.success('Nouveau lien généré — l\'ancien est révoqué');
    } catch { toast.error('Erreur'); } finally { setBusy(false); }
  };

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/catalogue/public-link`, { enabled: !link.enabled });
      setLink(r.data);
      toast.success(r.data.enabled ? 'Lien activé' : 'Lien désactivé');
    } catch { toast.error('Erreur'); } finally { setBusy(false); }
  };

  const waText = `Bonjour ! Voici notre catalogue pro en direct (prix détail, semi-gros et gros, stock à jour) :\n${pageUrl}\n\nVersion PDF illustrée : ${pdfUrl}`;

  return (
    <div className="border border-[color:rgb(var(--primary-rgb)/0.20)] bg-[color:rgb(var(--primary-rgb)/0.05)] p-4 space-y-3" data-testid="public-catalogue-link">
      <div className="flex items-center justify-between gap-3">
        <p className="text-white text-sm font-medium inline-flex items-center gap-2"><Link2 className="w-4 h-4 text-[color:var(--color-primary)]" /> Lien catalogue en ligne</p>
        <button type="button" onClick={toggle} disabled={busy}
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 border ${link.enabled ? 'border-emerald-500/40 text-emerald-400' : 'border-red-500/40 text-red-400'}`}
          data-testid="public-link-toggle">
          <Power className="w-3 h-3" /> {link.enabled ? 'Actif' : 'Désactivé'}
        </button>
      </div>
      <p className="text-zinc-500 text-xs">Se met à jour automatiquement (produits, prix, promos, stock). Donnez-le à votre IA WhatsApp ou à vos clients.</p>

      <div className="flex items-center gap-2">
        <input readOnly value={pageUrl} onFocus={e => e.target.select()} className="flex-1 bg-black/40 border border-white/10 text-zinc-300 text-xs px-2 py-2 outline-none min-w-0" data-testid="public-link-url" />
        <button type="button" onClick={() => copy(pageUrl, 'Lien')} className="p-2 border border-white/10 text-zinc-300 hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]" title="Copier" data-testid="public-link-copy"><Copy className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-2">
        <input readOnly value={pdfUrl} onFocus={e => e.target.select()} className="flex-1 bg-black/40 border border-white/10 text-zinc-400 text-xs px-2 py-2 outline-none min-w-0" data-testid="public-pdf-url" />
        <button type="button" onClick={() => copy(pdfUrl, 'Lien PDF')} className="p-2 border border-white/10 text-zinc-300 hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]" title="Copier le lien PDF" data-testid="public-pdf-copy"><FileDown className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <a href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[color:rgb(37_211_102/0.15)] border border-[color:rgb(37_211_102/0.40)] text-[#25D366] text-xs px-3 py-2 hover:bg-[color:rgb(37_211_102/0.25)]" data-testid="public-link-whatsapp">
          <MessageCircle className="w-4 h-4" /> Envoyer sur WhatsApp
        </a>
        <button type="button" onClick={regenerate} disabled={busy} className="inline-flex items-center gap-2 border border-white/10 text-zinc-400 text-xs px-3 py-2 hover:text-red-400 hover:border-red-500/40" data-testid="public-link-regenerate">
          <RefreshCw className="w-4 h-4" /> Régénérer (révoquer l'ancien)
        </button>
      </div>
    </div>
  );
};
